import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search, Flame, ShieldAlert, Clock, Wallet, X } from "lucide-react";
import { BioButton } from "@/components/bio/BioButton";
import { StatusBadge } from "@/components/bio/StatusBadge";
import { EmptyState } from "@/components/bio/EmptyState";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { formatUSD, formatDateTime } from "@/lib/format";

const RESTR = [
    { id: "gluten", label: "Sin gluten" },
    { id: "lacteos", label: "Sin lácteos" },
    { id: "frutos_secos", label: "Sin frutos secos" },
    { id: "vegetariano", label: "Vegetariano" },
];

export default function StudentsView() {
    const { user } = useAuth();
    const canEdit = user?.role === "super_admin" || user?.role === "school_admin";
    const [list, setList] = useState(null);
    const [q, setQ] = useState("");
    const [detail, setDetail] = useState(null);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ restrictions: [], daily_limit: 0 });
    const [saving, setSaving] = useState(false);

    const load = useCallback(() => {
        api.get("/students", { params: q ? { q } : {} }).then(({ data }) => setList(data));
    }, [q]);
    useEffect(() => { load(); }, [load]);

    const openDetail = async (s) => {
        setOpen(true);
        setDetail(null);
        const { data } = await api.get(`/students/${s.id}`);
        setDetail(data);
        setForm({ restrictions: data.student.restrictions || [], daily_limit: data.student.daily_limit });
    };

    const toggleR = (id) => setForm((f) => ({ ...f, restrictions: f.restrictions.includes(id) ? f.restrictions.filter((x) => x !== id) : [...f.restrictions, id] }));

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/students/${detail.student.id}`, form);
            setDetail((d) => ({ ...d, student: data }));
            setList((l) => l.map((x) => (x.id === data.id ? { ...x, ...data } : x)));
            toast.success("Perfil actualizado");
        } catch (e) {
            toast.error("No se pudo guardar");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-5">
            <div>
                <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Estudiantes</h1>
                <p className="text-bio-text-secondary mt-1 text-sm">Perfil, historial y restricciones alimentarias.</p>
            </div>
            <div className="relative max-w-sm">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-bio-text-muted" />
                <Input data-testid="student-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre o ID…" className="h-11 pl-10 rounded-bio bg-white border-[1.5px]" />
            </div>

            {!list ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 rounded-bio-card" />)}</div>
            ) : list.length === 0 ? (
                <div className="bg-white rounded-bio-card border border-bio-border-subtle"><EmptyState title="Sin estudiantes" /></div>
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {list.map((s) => (
                        <button key={s.id} data-testid={`student-${s.id}`} onClick={() => openDetail(s)} className="text-left bg-white rounded-bio-card border border-bio-border-subtle p-4 flex items-center gap-3 hover:border-bio-accent hover:shadow-bio-card transition-all">
                            <img src={s.photo} alt="" className="h-12 w-12 rounded-full bg-bio-bg" />
                            <div className="min-w-0 flex-1">
                                <p className="font-bold text-sm truncate">{s.name}</p>
                                <p className="text-xs text-bio-text-muted">{s.grade} · {s.bio_id}</p>
                                <p className="text-xs text-bio-primary font-semibold mt-0.5">{formatUSD(s.balance)}</p>
                            </div>
                            {s.restrictions?.length > 0 && <ShieldAlert className="h-4 w-4 text-[#B45309]" />}
                        </button>
                    ))}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="rounded-bio-lg max-w-lg border-bio-border-subtle max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Perfil del estudiante</DialogTitle>
                        <DialogDescription>Historial, límites y restricciones.</DialogDescription>
                    </DialogHeader>
                    {!detail ? (
                        <Skeleton className="h-64 w-full rounded-bio" />
                    ) : (
                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <img src={detail.student.photo} alt="" className="h-16 w-16 rounded-full bg-bio-bg" />
                                <div>
                                    <p className="font-bold text-lg">{detail.student.name}</p>
                                    <p className="text-sm text-bio-text-secondary">{detail.student.grade} · {detail.school?.name} · {detail.cafeteria?.name}</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                <div className="bg-bio-bg rounded-bio p-3"><p className="text-xs text-bio-text-muted">Saldo</p><p className="font-bold">{formatUSD(detail.student.balance)}</p></div>
                                <div className="bg-bio-bg rounded-bio p-3"><p className="text-xs text-bio-text-muted">Gastado total</p><p className="font-bold">{formatUSD(detail.stats.total_spent)}</p></div>
                                <div className="bg-bio-bg rounded-bio p-3"><p className="text-xs text-bio-text-muted">Compras</p><p className="font-bold">{detail.stats.purchase_count}</p></div>
                            </div>

                            <div className="border border-bio-border-subtle rounded-bio-card p-4">
                                <p className="text-sm font-bold mb-3">Restricciones y límite {canEdit ? "(editable)" : ""}</p>
                                <div className="grid grid-cols-2 gap-2">
                                    {RESTR.map((r) => (
                                        <label key={r.id} className="flex items-center gap-2 text-sm">
                                            <Checkbox data-testid={`restr-${r.id}`} checked={form.restrictions.includes(r.id)} onCheckedChange={() => canEdit && toggleR(r.id)} disabled={!canEdit} /> {r.label}
                                        </label>
                                    ))}
                                </div>
                                <div className="mt-3 flex items-center gap-2">
                                    <span className="text-sm">Límite diario (USD)</span>
                                    <Input data-testid="daily-limit-input" type="number" min="0" step="0.5" value={form.daily_limit} onChange={(e) => setForm((f) => ({ ...f, daily_limit: Number(e.target.value) }))} disabled={!canEdit} className="h-9 w-24 rounded-bio border-[1.5px]" />
                                </div>
                                {canEdit && <BioButton data-testid="student-save" className="w-full mt-4" loading={saving} onClick={save}>Guardar cambios</BioButton>}
                            </div>

                            <div>
                                <p className="text-sm font-bold mb-2 flex items-center gap-2"><Clock className="h-4 w-4 text-bio-primary" /> Historial reciente</p>
                                <div className="divide-y divide-bio-border-subtle max-h-56 overflow-y-auto">
                                    {detail.transactions.map((t) => (
                                        <div key={t.id} className="flex items-center justify-between py-2">
                                            <div className="min-w-0"><p className="text-sm truncate">{t.description}</p><p className="text-xs text-bio-text-muted">{formatDateTime(t.created_at)}</p></div>
                                            <div className="flex items-center gap-2 shrink-0"><span className={`text-sm font-semibold ${t.type === "recharge" ? "text-bio-success" : ""}`}>{t.type === "recharge" ? "+" : "−"}{formatUSD(t.amount)}</span><StatusBadge status={t.status} /></div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
